import React, { useEffect } from "react";
import styled from 'styled-components';
import { animate, stagger } from 'animejs';

import useIntersectionObserver from "../../hooks/useIntersectionObserver";

import { Link as RouterLink } from 'react-router-dom';
import Navbar from "../../components/navbar";

import {
    SectionContainer,
    SupplyChainContainer,
    SupportContainer,
    ToolItem
} from './home.css';

const MainTitle = styled.h1`
    color: #1E1E1E;
    font-family: 'Coolvetica';
    font-size: 4.3rem;
    font-weight: 400;
    text-shadow: 0px 0px 10px rgba(22, 22, 22, 0.5);
    line-height: 1;
    margin: 0;
    margin-top: 2rem;
    padding: 0 35px;

    @media (max-width: 768px) {
        font-size: 3.8rem;
    }
    @media (max-width: 480px) {
        font-size: 4rem;
    }
    @media (max-width: 360px) {
        font-size: 3rem;
    }
`;

const StepItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 35px;
    max-width: 250px;
    flex-wrap: wrap;

    img {
        width: 100px;
        height: 100px;
        margin-bottom: 0;
    } 

    h3 {
        font-family: 'Outfit';
        font-size: 1.2rem;
        color: #212121;
        font-weight: 600;
        margin-bottom: 0;
        text-align: center;
        text-shadow: 0px 0px 10px rgba(255, 255, 255, 0.5);
        width: 100%;
        max-width: 300px;
        line-height: 1.3;
        @media (max-width: 768px) {
            font-size: 1.2rem;
            width: 100%;
        }
        @media (max-width: 480px) {
            font-size: 1rem;
            width: 100%;
        }
        @media (max-width: 360px) {
            font-size: 0.9rem;
            width: 100%;
        }
    }

    p {
        font-family: 'Outfit';
        font-size: 1rem;
        color: #212121;
        font-weight: 400;
        text-align: center;
        line-height: 1.5;
        width: 100%;
        max-width: 300px;
        @media (max-width: 768px) {
            font-size: 0.9rem;
            width: 100%;
        }
        @media (max-width: 480px) {
            font-size: 0.8rem;
            width: 100%;
        }
        @media (max-width: 360px) {
            font-size: 0.7rem;
            width: 100%;
        }
    }
`;

const PartnersList = styled.div`
    width: 100%;
    max-width: 1080px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    margin-top: 50px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 40px;
        padding: 0 20px;
    }
`;

const PartnerLogo = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }

    @media (max-width: 768px) {
        width: 100%;
        max-width: 200px;
    }
`;

const toolLogos = [
    '/assets/tools/logo-salesforce.png',
    '/assets/tools/logo-oracle.png',
    '/assets/tools/logo-whatsapp.png',
    '/assets/tools/logo-xlsx.png',
    '/assets/tools/logo-netsuite.png',
    '/assets/tools/logo-gmail.png',
    '/assets/tools/logo-notion.png',
    '/assets/tools/logo-odoo.png',
    '/assets/tools/logo-messenger.png',
    '/assets/tools/logo-google-drive.png',
    '/assets/tools/logo-sap.png',
    '/assets/tools/logo-slack.png',
    '/assets/tools/logo-microsoft-teams.png',
    '/assets/tools/logo-prestashop.png',
    '/assets/tools/logo-drupal.png',
    '/assets/tools/logo-shopify.png',
    '/assets/tools/logo-wordpress.png',

];

export const Carousel = () => (
  <div style={{
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    height: 80,
    margin: '2rem 0'
  }}>
    <div
      className="carousel-track"
      style={{
        display: 'flex',
        width: 'fit-content',
        animation: 'scroll 30s linear infinite'
      }}
    >
      {[...toolLogos, ...toolLogos].map((logo, idx) => (
        <div
          key={idx}
          style={{
            minWidth: 80,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 24,
            backgroundImage: `url(${logo})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            width: 80,
          }}
        />
      ))}
    </div>
    <style>
      {`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .carousel-track {
          will-change: transform;
        }
      `}
    </style>
  </div>
);

const Home = () => {
    const [mainSectionRef, isMainSectionVisible] = useIntersectionObserver({
        threshold: 0.3,
        rootMargin: '-50px'
    });

    const [, isProductsSectionVisible] = useIntersectionObserver({
        threshold: 0.3,
        rootMargin: '-50px'
    });

    const [, isServicesSectionVisible] = useIntersectionObserver({
        threshold: 0.3,
        rootMargin: '-50px'
    });

    const [, isPortfolioSectionVisible] = useIntersectionObserver({
        threshold: 0.3,
        rootMargin: '-50px'
    });

    useEffect(() => {
        const base = window.location.origin;
        const slug = "macarons-shop";
        const iframeSrc = base + "/chatbot-mini/" + encodeURIComponent(slug);
        let open = false;
        const root = document.createElement("div");
        root.id = "synaptek-chatbot-root";
        root.style.cssText = "position:fixed;bottom:24px;right:24px;z-index:999999;font-family:sans-serif;";
        const btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute("aria-label", "Ouvrir le chat");
        btn.style.cssText = "width:56px;height:56px;border-radius:50%;border:none;background:linear-gradient(135deg,#64B5F6,#0D47A1);color:#fff;cursor:pointer;box-shadow:0 4px 20px rgba(13,71,161,0.4);display:flex;align-items:center;justify-content:center;transition:transform 0.2s;";
        btn.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>';
        btn.onmouseover = () => { btn.style.transform = "scale(1.08)"; };
        btn.onmouseout = () => { btn.style.transform = "scale(1)"; };
        const frame = document.createElement("iframe");
        frame.src = iframeSrc;
        frame.title = "Chatbot";
        frame.style.cssText = "display:none;position:absolute;bottom:70px;right:0;width:420px;height:600px;max-height:80vh;border:none;border-radius:12px;box-shadow:0 4px 24px rgba(0,0,0,0.15);";
        btn.onclick = () => {
            open = !open;
            frame.style.display = open ? "block" : "none";
        };
        root.appendChild(btn);
        root.appendChild(frame);
        document.body.appendChild(root);
        return () => {
            if (root.parentNode) root.parentNode.removeChild(root);
        };
    }, []);

    useEffect(() => {
        animate('#main-title', {
            opacity: isMainSectionVisible ? 1 : 0,
            translateY: isMainSectionVisible ? 0 : -50,
            duration: 500,
            easing: 'easeInOutQuad',
        });

        animate('#main-secondary-title .typing-text-char', {
            opacity: isMainSectionVisible ? 1 : 0,
            translateY: isMainSectionVisible ? 0 : -50,
            duration: 50,
            easing: 'linear',
            delay: stagger(25, {  
                reversed: !isMainSectionVisible,
            }),
        });

        // animate('#main-cta', {
        //     opacity: isMainSectionVisible ? 1 : 0,
        //     duration: 500,
        //     easing: 'linear',
        //     delay: 100
        // });

        // animate('#main-section p', {
        //     opacity: isMainSectionVisible ? 1 : 0,
        //     duration: 500,
        //     easing: 'linear',
        //     delay: 200
        // });
    }, [isMainSectionVisible]);

    useEffect(() => {
        animate('#chat-section h1', {
            opacity: isProductsSectionVisible ? 1 : 0,
            translateY: isProductsSectionVisible ? 0 : -50,
            duration: 500,
            easing: 'easeInOutQuad',
            delay: stagger(100, { start: 0 }),
        });

        animate('#chat-section article', {
            opacity: isProductsSectionVisible ? 1 : 0,
            translateY: isProductsSectionVisible ? 0 : -50,
            duration: 500,
            easing: 'easeInOutQuad',
            delay: stagger(100, { start: 200 }),
        });

        animate('.product-item', {
            opacity: isProductsSectionVisible ? 1 : 0,
            translateY: isProductsSectionVisible ? 0 : -50,
            duration: 500,
            easing: 'easeInOutQuad',
            delay: stagger(100, { start: 500 }),
        });
    }, [isProductsSectionVisible]);

    useEffect(() => {
        if (isPortfolioSectionVisible) {
            animate('#portfolio-title', {
                opacity: [0, 1],
                translateY: [-30, 0],
                duration: 800,
                easing: 'easeOutQuad',
            });

            animate('#portfolio-list .portfolio-item', {
                opacity: [0, 1],
                translateY: [-20, 0],
                duration: 600,
                easing: 'easeOutQuad',
                delay: stagger(200, { 
                    start: 300,
                    from: 'start'
                }),
            });
        } else {
            animate('#portfolio-title', {
                opacity: 0,
                translateY: -30,
                duration: 0,
            });

            animate('#portfolio-list .portfolio-item', {
                opacity: 0,
                translateY: -20,
                duration: 0,
            });
        }
    }, [isPortfolioSectionVisible]);

    useEffect(() => {
        animate('#services-title .typing-text-char', {
            opacity: isServicesSectionVisible ? 1 : 0,
            translateY: isServicesSectionVisible ? 0 : 0,
            duration: 50,
            easing: 'linear',
            delay: stagger(150, {  
                reversed: !isServicesSectionVisible,
            }),
        });

        animate('.typing-pointer', {
            duration: 500,
            keyframes: [
                { opacity: 1, duration: 150 },
                { opacity: 1, duration: 500 },
                { opacity: 0, duration: 150 },
                { opacity: 0, duration: 500 }
            ],            
            easing: 'linear',
            loop: true,
            direction: 'alternate',
            delay: 1750,
        });

        animate('.service-item', {
            opacity: isServicesSectionVisible ? 1 : 0,
            duration: 500,
            easing: 'easeInOutQuad',
            delay: stagger(100, { start: 0 }),
        });
    }, [isServicesSectionVisible]);

    return (
        <div style={{
            backgroundImage: "url('/assets/background-home.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
            width: '100vw',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            <Navbar />

            <section 
                id="main-section" 
                ref={mainSectionRef}
                style={{
                    display: "flex",
                    height: 'auto',
                    width: "100vw",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: 'transparent',
                    position: 'relative',
                    paddingTop: '7rem',
                    paddingBottom: '3rem'
                }}
            >
                
                <img 
                    src="/assets/logo-synaptek-chatbot.png"
                    alt="Logo ChatGPT"
                    style={{
                        left: '25px',
                        width: '350px',
                        height: '350px',
                        zIndex: 10,
                        // animation: 'spin 10s linear infinite'
                    }}
                />

                <MainTitle id="main-title">
                    Stratégie IA pour PME / PMI<br/>
                    à Annecy
                </MainTitle>

                <h2
                    style={{
                        lineHeight: 1.5,
                        borderRadius: '25px',
                        marginTop: '2rem',
                        fontFamily: 'Outfit',
                        fontSize: '1.5rem',
                        color: '#212121',
                        textAlign: 'center',
                        maxWidth: '650px',
                        padding: '0 35px',
                        fontWeight: 500
                    }}
                >
                    Nous concevons vos outils IA sur-mesure et vous accompagnons dans votre transformation digitale pour booster votre productivité. ⚡
                </h2>
            </section>

            <section style={{
                width: '100%',
                paddingBottom: '5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
            }}>

                {/* STEPS LIST  */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'row',
                    flexWrap: 'wrap'
                }}>
                    <StepItem>
                        <img
                            src='/assets/robot-step-1.png'
                            alt='Robot étape 1'
                        />
                        <h3>1 • Conseil en intégration</h3>
                        <p>
                            Nous auditons vos processus pour cibler les cas d’usage à fort ROI et bâtissons une 
                            feuille de route concrète pour faire de l’IA un levier de croissance.
                        </p>
                    </StepItem>
                    <StepItem>
                        <img
                            src='/assets/robot-step-2.png'
                            alt='Robot étape 1'
                        />
                        <h3>2 • Conception</h3>
                        <p>
                            Nous développons des outils sur-mesure (agents, analyse de documents) qui automatisent 
                            les tâches chronophages et recentrent vos équipes sur leur cœur de métier.
                        </p>
                    </StepItem>
                    <StepItem>
                        <img
                            src='/assets/robot-step-3.png'
                            alt='Robot étape 1'
                        />
                        <h3>3 • Local</h3>
                        <p>
                            Ancrés à Annecy, nous intégrons les solutions dans votre écosystème et assurons 
                            maintenance, sécurité des données et évolution de vos outils.
                        </p>
                    </StepItem>
                </div>

                <div style={{
                    marginTop: '3rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <RouterLink to="/onboarding" style={{
                        backgroundColor: '#051923',
                        color: '#fff',
                        padding: '0 1.5rem',
                        border: 'none',
                        fontSize: '1.3rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease',
                        fontFamily: 'Outfit',
                        fontWeight: 600,
                        height: '50px',
                        lineHeight: '2.5',
                        borderRadius: '25px',
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.7)',
                        textDecoration: 'none'
                    }}>
                        Créer mon chatbot IA
                    </RouterLink>
                    <span style={{
                        color: '#3F3F3F',
                        fontFamily: 'Montserrat',
                        fontWeight: 400,
                        marginTop: '10px',
                        fontSize: '0.8rem',
                        fontStyle: 'italic',
                        display: 'none'
                    }}>
                        ou contactez moi directement par email ou téléphone
                    </span>

                </div>
            </section>

            <section style={{
                display: 'flex',
                alignItems: 'center'
            }}>
                <SectionContainer>

                    <MainTitle id="industry-title" style={{
                        fontSize: '2.5rem',
                        textAlign: 'left',
                        marginTop: 0,
                        padding: 0
                    }}>
                        Chatbot IA pour le commerce
                    </MainTitle>

                    <h2>
                        Assistant virtuel pour le commerce de détail
                    </h2>
                    <p>
                        Gagnez du temps sur vos choix techniques et achats.<br/>
                        Notre assistant intelligent répond à vos questions sur les aciers, traitements, normes et tolérances.<br/>
                        Formé sur vos catalogues et datasheets, il comprend votre langage métier.<br/>
                        <br/>
                        🔍 Recherchez dans vos documents techniques en langage naturel.<br/>
                        ⚙️ Obtenez des recommandations matière adaptées à vos contraintes.
                    </p>

                    <div className="prompt-example">
                        « Quelle nuance d'acier est la plus adaptée pour optimiser le coût d'usinage d'une pièce en grande série ? »
                    </div>
                    <div className='prompt-example' style={{
                        marginTop: '1rem'
                    }}>
                        « Quels fournisseurs proposent cette matière avec certification EN 10204 3.1 et livraison rapide ? »
                    </div>


                    <div style={{
                        display: 'none'
                    }}>
                        <a href="/chatbot-industrie">
                            Essayer notre chatbot industrie
                        </a>
                    </div>

                </SectionContainer>
            </section>

            <section style={{
                display: 'none'
            }}>
                <SectionContainer>

                    <MainTitle id="industry-title" style={{
                        fontSize: '2.5rem',
                        textAlign: 'left',
                        marginTop: 0,
                        padding: 0
                    }}>
                        Chatbot IA pour l'industrie
                    </MainTitle>

                    <h2>
                        Chatbot IA pour l'industrie du décolletage
                    </h2>
                    <p>
                        Gagnez du temps sur vos choix techniques et achats.<br/>
                        Notre assistant intelligent répond à vos questions sur les aciers, traitements, normes et tolérances.<br/>
                        Formé sur vos catalogues et datasheets, il comprend votre langage métier.<br/>
                        <br/>
                        🔍 Recherchez dans vos documents techniques en langage naturel.<br/>
                        ⚙️ Obtenez des recommandations matière adaptées à vos contraintes.
                    </p>

                    <div className="prompt-example">
                        « Quelle nuance d'acier est la plus adaptée pour optimiser le coût d'usinage d'une pièce en grande série ? »
                    </div>
                    <div className='prompt-example' style={{
                        marginTop: '1rem'
                    }}>
                        « Quels fournisseurs proposent cette matière avec certification EN 10204 3.1 et livraison rapide ? »
                    </div>


                    <div style={{
                        display: 'none'
                    }}>
                        <a href="/chatbot-industrie">
                            Essayer notre chatbot industrie
                        </a>
                    </div>

                </SectionContainer>
            </section>

            <section>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem'
                }}>

                    <h3 style={{
                        fontFamily: 'Outfit',
                        fontSize: '1.5rem',
                        color: '#212121',
                        textAlign: 'center',
                        fontWeight: 500,
                        marginBottom: 0
                    }}>
                        Sur la base de vos outils existants :
                    </h3>

                    <Carousel />

                </div>
            </section>

            <section style={{
                display: 'flex',
                alignItems: 'center'
            }}>
                <SupplyChainContainer className="align-right">

                    <MainTitle id="industry-title" style={{
                        fontSize: '2.5rem',
                        marginTop: 0,
                        padding: 0
                    }}>
                        L'IA au service de votre supply chain
                    </MainTitle>

                    <h2>
                        Chatbot IA pour les équipes achats, approvisionnement et logistique
                    </h2>
                    <p>
                        Prenez de meilleures décisions, plus rapidement.<br/>
                        Notre assistant intelligent répond à vos questions sur les fournisseurs, délais, tarifs, contrats et historiques de commandes.<br/>
                        Formé sur vos documents internes (ERP, SLA, catalogues, procédures), il comprend vos enjeux opérationnels.<br/><br/>

                        🔍 Interrogez vos données supply chain en langage naturel.<br/>
                        🚚 Obtenez des réponses fiables pour optimiser vos achats et livraisons.
                    </p>

                    <div style={{
                        display: 'flex',
                        alignItems: 'end',
                        flexDirection: 'column'
                    }}>
                        <div className="prompt-example">
                            « Quels fournisseurs respectent nos délais contractuels pour cette référence ? »
                        </div>
                        <div className='prompt-example' style={{
                            marginTop: '1rem'
                        }}>
                            « Quelle est la tendance de prix de ce composant sur les 12 derniers mois ? »
                        </div>
                    </div>

                    <div style={{
                        display: 'none'
                    }}>
                        <a href="/configurer">
                            Essayer notre chatbot supply chain
                        </a>
                    </div>

                </SupplyChainContainer>
            </section>

            <section>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem'
                }}>

                    <h3 style={{
                        fontFamily: 'Outfit',
                        fontSize: '1.5rem',
                        color: '#212121',
                        textAlign: 'center',
                        fontWeight: 500,
                        marginBottom: 25
                    }}>
                        Propulsé par :
                    </h3>

                    <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <ToolItem style={{ backgroundImage: 'url(/assets/tools/logo-chatgpt.png)' }} />
                        <ToolItem style={{ backgroundImage: 'url(/assets/tools/logo-gemini.png)' }} />
                        <ToolItem style={{ backgroundImage: 'url(/assets/tools/logo-deepseek.png)' }} />
                        <ToolItem style={{ backgroundImage: 'url(/assets/tools/logo-grok.png)' }} />
                    </div>

                </div>
            </section>


            <section style={{
                display: 'flex',
                alignItems: 'center'
            }}>
                <SupportContainer>

                    <MainTitle id="industry-title" style={{
                        fontSize: '2.5rem',
                        marginTop: 0,
                        padding: 0,
                        textAlign: 'left'
                    }}>
                        L'IA au service du support technique
                    </MainTitle>

                    <h2>
                        Chatbot IA pour l'assistance client et le support B2B
                    </h2>
                    <p>
                        Réduisez vos délais de réponse, même sur les demandes complexes.<br/>
                        Notre assistant intelligent comprend vos produits, vos procédures et vos cas clients.
                        Formé sur vos bases de connaissances, tickets historiques, manuels et FAQ internes, il apporte des réponses fiables en temps réel.<br/><br/>

                        🔧 Résolvez les incidents plus vite avec une recherche technique en langage naturel.<br/>
                        📘 Améliorez la cohérence des réponses entre vos équipes support, où qu'elles soient.
                    </p>

                    <div className="prompt-example">
                        « Quelle procédure suivre pour diagnostiquer un défaut sur ce modèle ? »
                    </div>
                    <div className='prompt-example' style={{
                        marginTop: '1rem'
                    }}>
                        « Ce client a déjà ouvert un ticket sur ce sujet, que lui avons-nous répondu ? »
                    </div>

                    <div style={{
                        display: 'none'
                    }}>
                        <a href="https://macarons-shop.site/boutique">
                            Essayer notre chatbot support
                        </a>
                    </div>

                </SupportContainer>
            </section>

            <section id="partners-section" style={{
                width: "100vw",
                display: 'none',
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                backgroundColor: "transparent",
                color: "#fff",
                paddingTop: '80px',
                paddingBottom: '80px',
            }}>
                <h1 style={{
                    color: "#fff",
                    fontFamily: 'Coolvetica',
                    fontSize: "50px",
                    fontWeight: 400,
                    textShadow: '0px 0px 10px rgba(255, 255, 255, 0.5)',
                    textAlign: 'center'
                }}>
                    Ils nous font confiance
                </h1>
                
                <PartnersList>
                    <PartnerLogo>
                        <img
                            src="/assets/logo-bsa-group.png"
                            alt="BSA Group"
                            width={100*(490/283)}
                            height={100}
                        />
                    </PartnerLogo>
                    <PartnerLogo>
                        <img
                            src="/assets/logo-popnrest.png"
                            alt="Pop&Rest"
                            width={100*(312/283)}
                            height={100}
                        />
                    </PartnerLogo>
                    <PartnerLogo>
                        <img
                            src="/assets/logo-aci.png"
                            alt="BIL Assurances"
                            width={80*(744/278)}
                            height={80}
                        />
                    </PartnerLogo>
                    <PartnerLogo>
                        <img
                            src="/assets/logo-vyv.png"
                            alt="AWS"
                            width={100*(307/284)}
                            height={100}
                        />
                    </PartnerLogo>
                </PartnersList>
            </section>

            <section id="contact-section" style={{
                width: "100%",
                height: "auto",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#05080D",
                color: "#fff",
                display: "flex",
                paddingTop: '80px',
                paddingBottom: '80px'
            }}>
                <h1 id="services-title" style={{
                    color: "#fff",
                    fontFamily: 'Coolvetica',
                    fontSize: "50px",
                    fontWeight: 400,
                    textShadow: '0px 0px 10px rgba(255, 255, 255, 0.5)',
                    margin: 0
                }}>
                    Contactez-nous
                </h1>    

                <div style={{
                    marginTop: '30px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    fontFamily: 'Montserrat',
                    fontSize: '1rem',
                    color: '#FFFFFF',
                    lineHeight: '20px',
                    textAlign: 'center',
                }}>
                    hello@synaptek.fr<br />
                    <br />
                    +33 (0)6 26 52 18 44<br />
                    <br />
                    19 Chemin de Charnay<br />
                    74940 Annecy-le-Vieux<br />
                    Haute-Savoie, France
                </div>

                <RouterLink
                    to="/onboarding"
                    style={{
                        backgroundColor: '#FFFFFF',
                        color: '#051923',
                        padding: '0 1.5rem',
                        border: 'none',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease',
                        fontFamily: 'Montserrat',
                        fontWeight: 700,
                        lineHeight: '2.5rem',
                        borderRadius: '35px',
                        marginTop: '35px',
                        textDecoration: 'none'
                    }}
                >
                    Rencontrons-nous
                </RouterLink>
            </section>

        </div>
    );
}

export default Home;
